import { CohortProgramCard } from "@/components/academician/cohort-program-card"
import { ArrowRightIcon } from "lucide-react"
import { Link } from "react-router-dom"

interface Cohort {
  name: string;
  status: "enrolling" | "starting-soon" | "in-progress" | "completed";
  startDate: string;
  duration: string;
  price: string;
  enrolled: number;
  capacity: number;
  bannerImage?: string;
  href?: string;
}

interface CohortsSectionProps {
  /**
   * Section title
   */
  title?: string;

  /**
   * Section description
   */
  description?: string;

  /**
   * Array of cohorts to display
   */
  cohorts?: Cohort[];

  /**
   * Show "View All Cohorts" link
   */
  showViewAll?: boolean;

  /**
   * View all link URL
   */
  viewAllHref?: string;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export function CohortsSection({
  title = "Current Cohorts",
  description = "Join a cohort starting soon and learn with fellow founders",
  cohorts = [],
  showViewAll = true,
  viewAllHref = "/cohorts",
  className = ""
}: CohortsSectionProps) {
  return (
    <section className={`bg-muted/30 py-16 md:py-24 ${className}`}>
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {title}
          </h2>
          <p className="text-lg text-muted-foreground">
            {description}
          </p>
        </div>

        {/* Cohorts Grid */}
        {cohorts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-8">
            {cohorts.map((cohort, index) => (
              <CohortProgramCard
                key={index}
                name={cohort.name}
                status={cohort.status}
                startDate={cohort.startDate}
                duration={cohort.duration}
                price={cohort.price}
                enrolled={cohort.enrolled}
                capacity={cohort.capacity}
                bannerImage={cohort.bannerImage}
                href={cohort.href}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No cohorts available at the moment.</p>
          </div>
        )}

        {/* View All Link */}
        {showViewAll && cohorts.length > 0 && (
          <div className="text-center">
            <Link
              to={viewAllHref}
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-semibold text-lg transition-colors group"
            >
              View All Cohorts
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        )}
      </div>
    </section>
  )
}
